import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import "./login.css";
import BackgroundImage from "./fundo.jpg";
import Logo from "./logo.png";

const Login = () => {
  const [inputUsername, setInputUsername] = useState("");
  const [inputPassword, setInputPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('error');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  useEffect(() => {
    // Verificar se há credenciais salvas
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedExpiry = localStorage.getItem('rememberExpiry');

    if (savedUsername && savedExpiry) {
      const expiryDate = new Date(savedExpiry);
      const now = new Date();

      if (now < expiryDate) {
        // Credenciais ainda válidas
        setInputUsername(savedUsername);
        setRememberMe(true);
      } else {
        // Credenciais expiradas, limpar
        localStorage.removeItem('rememberedUsername');
        localStorage.removeItem('rememberExpiry');
      }
    }

    const ttChatLoaderS = document.createElement('script');
    document.tomticketChatLoaderScriptVersion = 2;
    ttChatLoaderS.src =
      'https://athix.tomticket.com/scripts-chat/chat.min.js' +
      '?id=EP67940' +
      '&account=4348160P05062025074357' +
      '&autoOpen=0' +
      '&hideWhenOffline=0' +
      '&d=athix' +
      '&ts=' + new Date().getTime() +
      '&ref=' + encodeURIComponent(document.URL);
    ttChatLoaderS.async = true;
    document.body.appendChild(ttChatLoaderS);

    return () => {
      document.body.removeChild(ttChatLoaderS);
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const resposta = await fetch(`${process.env.REACT_APP_API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usuario: inputUsername,
          senha: inputPassword
        })
      });

      const dados = await resposta.json();

      if (!resposta.ok) {
        throw new Error(dados.mensagem || 'Erro desconhecido ao tentar logar.');
      }

      // Salvar token
      localStorage.setItem('token', dados.token);

      // Se "Lembrar-me" estiver marcado, salvar usuário por 30 dias
      if (rememberMe) {
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 30); // 30 dias
        localStorage.setItem('rememberedUsername', inputUsername);
        localStorage.setItem('rememberExpiry', expiryDate.toISOString());
      } else {
        // Se desmarcado, limpar dados salvos
        localStorage.removeItem('rememberedUsername');
        localStorage.removeItem('rememberExpiry');
      }

      // Mostrar mensagem de sucesso
      setSnackMessage('Login realizado com sucesso! Redirecionando...');
      setSnackSeverity('success');
      setSnackOpen(true);

      // Redirecionar após 1 segundo
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);

    } catch (error) {
      console.error('Erro ao fazer login:', error.message);

      if (error.message.includes('senha') || error.message.includes('usuário')) {
        setSnackMessage('Usuário ou senha incorretos. Verifique suas credenciais!');
      } else if (error.message.includes('NetworkError')) {
        setSnackMessage('Servidor indisponível no momento. Tente novamente mais tarde.');
      } else {
        setSnackMessage(error.message || 'Erro inesperado.');
      }
      setSnackSeverity('error');
      setSnackOpen(true);

    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="sign-in__wrapper">
      {/* Lado Esquerdo - Imagem de Fundo */}
      <Box
        className="sign-in__left"
        style={{ backgroundImage: `url(${BackgroundImage})` }}
      >
        <div className="sign-in__backdrop" />
        <Box className="sign-in__left-content">
          <img
            src={Logo}
            alt="EASY Locadora"
            style={{
              width: "180px",
              marginBottom: "2rem",
              filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.3))",
            }}
          />
        
          <Typography variant="body1" component="p">
            Gestão completa para sua locadora de veículos
          </Typography>
        </Box>
      </Box>

      {/* Lado Direito - Formulário */}
      <Box className="sign-in__right">
        <Box component="form" className="login__form" onSubmit={handleSubmit}>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: "#201d1e",
              textAlign: "center",
            }}
          >
            Bem-vindo de volta!
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 3,
              color: "#666",
              textAlign: "center",
            }}
          >
            Entre com suas credenciais para acessar o sistema
          </Typography>

          <TextField
            label="Usuário"
            variant="outlined"
            fullWidth
            value={inputUsername}
            onChange={(e) => setInputUsername(e.target.value)}
            sx={{
              mb: 2,
              "& label.Mui-focused": {
                color: "#FF8C00",
              },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#FF8C00",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FF8C00",
                },
              },
            }}
          />

          <TextField
            label="Senha"
            type="password"
            variant="outlined"
            fullWidth
            value={inputPassword}
            onChange={(e) => setInputPassword(e.target.value)}
            sx={{
              mb: 2,
              "& label.Mui-focused": {
                color: "#FF8C00",
              },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#FF8C00",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FF8C00",
                },
              },
            }}
          />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{
                    color: "#FF8C00",
                    "&.Mui-checked": {
                      color: "#FF8C00",
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" color="#666">
                  Lembrar-me (30 dias)
                </Typography>
              }
            />
            <Typography
              variant="body2"
              sx={{
                color: "#FF8C00",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
              onClick={() => setForgotPasswordOpen(true)}
            >
              Esqueci a senha
            </Typography>
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              backgroundColor: "#FF8C00",
              fontWeight: "bold",
              borderRadius: 2,
              padding: "12px",
              textTransform: "none",
              fontSize: "1rem",
              boxShadow: "0 4px 12px rgba(255,140,0,0.3)",
              "&:hover": {
                backgroundColor: "#e07b00",
                boxShadow: "0 6px 16px rgba(255,140,0,0.4)",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
            }}
          >
            {loading ? "Entrando..." : "Entrar"}
          </Button>
        </Box>

        {/* Rodapé */}
        <Box className="login__footer">
          <Typography variant="caption">
            EASY Locadora de Veículos - Manaus/AM © {new Date().getFullYear()}{" "}
            - v1.0.0
            <br />
            Desenvolvido por ATHIX
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackSeverity}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>

      {/* Modal Esqueci a Senha */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#201d1e" }}>
          Esqueceu sua senha?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Para recuperar o acesso à sua conta, entre em contato com o suporte da ATHIX.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
            📱 <strong>Entre no grupo de WhatsApp:</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
            Clique no link abaixo para solicitar suporte e redefinir sua senha:
          </Typography>
          <Button
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#25D366",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#128C7E",
              },
            }}
            href="https://chat.whatsapp.com/seu-link-aqui"
            target="_blank"
            rel="noopener noreferrer"
          >
            📲 Abrir Grupo do WhatsApp
          </Button>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setForgotPasswordOpen(false)}
            sx={{ color: "#FF8C00" }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
